// NPM
import { defineStore } from 'pinia';

// Services
import Api from '@/services/api';

import type {
  UserDashboardLeaveCampaignHelperRequestType,
  UserDashboardUpdateCampaignHelperSettingsRequestType,
  ProspectDashboardGetSupportAdminResponseType,
  ProspectDashboardGetCampaignInfoResponseType,
  UserDashboardGetCampaignInfoResponseType,
  UserDashboardGetHelperCampaignsResponseType,
  UserDashboardGetHelperSignedUpResponseType,
  UserDashboardGetUserCriteriaResponseType,
  UserDashboardGetUserResponseType,
  UserDashboardGetUserTouchPointsResponseType,
  ProspectDashboardGetProspectTouchPointsResponseType,
  UserDashboardJoinCampaignHelperRequestType,
} from '@magma-app/magma-types/all';

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null as UserDashboardGetUserResponseType | null,
    isProspect: false as boolean,
    userCriteria: [] as UserDashboardGetUserCriteriaResponseType,
    userTouchpoints: [] as UserDashboardGetUserTouchPointsResponseType,
    allCampaigns: [] as UserDashboardGetHelperCampaignsResponseType,
    campaigns: [] as UserDashboardGetHelperSignedUpResponseType,
    campaignInfo: {} as
      | UserDashboardGetCampaignInfoResponseType
      | ProspectDashboardGetCampaignInfoResponseType,
    helpeeTouchpoints:
      [] as ProspectDashboardGetProspectTouchPointsResponseType,
    referFormEnabled: false as boolean,
    sharingLink: null as string | null,
    sharingPageEnabled: false as boolean,
    supportData: null as ProspectDashboardGetSupportAdminResponseType | null,
  }),

  actions: {
    async getAllCampaigns() {
      const response = await Api.userDashboard().campaign.getAllCampaigns();

      this.allCampaigns = response.data;
    },

    async getCampaigns() {
      const response = await Api.userDashboard().campaign.getCampaigns();

      this.campaigns = response.data;

      this.campaigns.forEach((campaign) => {
        if (campaign.sharingPageEnabled) {
          this.sharingPageEnabled = true;
        }
      });
    },

    async getCampaignInfoHelper(relationId: number, isProspect = false) {
      const response = await Api.userDashboard().campaign.getCampaignInfoHelper(
        relationId,
        isProspect
      );

      this.campaignInfo = response.data;
    },

    async getCampaignInfoProspect(relationId: number) {
      const response =
        await Api.userDashboard().campaign.getCampaignInfoProspect(relationId);

      this.campaignInfo = response.data;
    },

    async updateCampaign(
      payload: UserDashboardUpdateCampaignHelperSettingsRequestType
    ) {
      await Api.userDashboard().campaign.updateCampaign(payload);
    },

    async joinCampaign(payload: UserDashboardJoinCampaignHelperRequestType) {
      const response = await Api.userDashboard().campaign.joinCampaign(payload);

      this.allCampaigns = response.data;
    },

    async leaveCampaign(payload: UserDashboardLeaveCampaignHelperRequestType) {
      await Api.userDashboard().campaign.leaveCampaign(payload);

      // Refresh campaigns data after leaving a campaign
      await this.getAllCampaigns();
    },

    async getUser() {
      const response = await Api.userDashboard().user.getUser();

      this.user = response.data;
    },

    // the support widget is optional: a missing support contact must never block the dashboard
    async getSupportData(relationId?: number, campaignId?: number) {
      try {
        const response: { data: ProspectDashboardGetSupportAdminResponseType } =
          await Api.userDashboard().user.getSupportData(relationId, campaignId);

        this.supportData = response.data;
      } catch {
        this.supportData = null;
      }
    },

    async getSharingLink() {
      const response = await Api.userDashboard().user.getSharingLink();

      this.sharingLink = response.data.shortUrl || null;
    },

    async uploadPicture(payload: { profilePicture: File }) {
      const response =
        await Api.userDashboard().user.uploadProfilePicture(payload);

      if (this.user) {
        this.user.profilePictureUrl = response.data.profilePictureUrl;
      }
    },

    async updateUser(payload: {
      firstname?: string;
      lastname?: string;
      phoneNumber?: string;
      notificationEmail?: string;
      acceptPhoneNotifications?: boolean | undefined;
      acceptEmailNotifications?: boolean | undefined;
      position?: string;
      language?: string;
      address?: string | null;
      googlePlaceId?: string | null;
    }) {
      const response = await Api.userDashboard().user.updateUser(payload);

      this.user = {
        ...(this.user as UserDashboardGetUserResponseType),
        ...response.data,
      };
    },

    updateUserBalance(userBalance: number) {
      if (this.user) {
        this.user.balance = userBalance;
      }
    },

    async getUserCriteria() {
      const response = await Api.userDashboard().user.getUserCriteria();

      this.userCriteria = response.data;
    },

    async updateUserCriteria(payload: {
      criteriaId: number;
      answerKey?: string;
      answerKeys?: string[];
    }) {
      const response =
        await Api.userDashboard().user.updateUserCriteria(payload);

      if (this.userCriteria) {
        const criteria = this.userCriteria.find((criteria) => {
          return criteria.id === response.data.criteriaId;
        });

        if (criteria) {
          criteria.userAnswerKey =
            response.data.answerKey || criteria.userAnswerKey;
          criteria.userAnswerKeys =
            response.data.answerKeys || criteria.userAnswerKeys;
        }
      }
    },

    async getUserTouchpoints() {
      const response = await Api.userDashboard().user.getUserTouchpoints();

      this.userTouchpoints = response.data;
    },

    async getHelpeeTouchpoints() {
      const response = await Api.userDashboard().user.getHelpeeTouchpoints();

      this.helpeeTouchpoints = response.data;
    },

    async updateUserTouchpoint(payload: {
      touchPointId: number;
      value: string;
    }) {
      const response =
        await Api.userDashboard().user.updateUserTouchpoint(payload);

      if (this.userTouchpoints) {
        const touchpoint = this.userTouchpoints.find((touchpoint) => {
          return touchpoint.id === response.data.touchPointId;
        });

        if (touchpoint) {
          touchpoint.userTouchPointValue = response.data.value;
        }
      }
    },

    async updateProspectTouchpoint(payload: {
      touchPointId: number;
      value: string;
    }) {
      const response =
        await Api.userDashboard().user.updateProspectTouchpoint(payload);

      if (this.helpeeTouchpoints) {
        const touchpoint = this.helpeeTouchpoints.find((touchpoint) => {
          return touchpoint.id === response.data.touchPointId;
        });

        if (touchpoint) {
          touchpoint.value = response.data.value;
        }
      }
    },

    async sendSupportMessage(payload: { message: string; adminId: number }) {
      await Api.userDashboard().user.sendSupportMessage(payload);
    },
  },
  // Persist only the role flag so routing can discriminate across reloads
  persist: {
    paths: ['isProspect'],
  },
});
